import ms from 'ms'
import { createSelector } from 'reselect'

import { BUMP_RATE } from '../constants.js'
import { isEthereumLike } from '../utils/index.js'

const MINUTE = ms('1m')
const BumpType = {
  NONE: 0,
  CPFP: 1,
  RBF: 2,
}

// Legacy compatibility with wallets
export function getPendingNonExchangeTxs(activeWalletAccount, getTxLog, getIsExchangeTx) {
  console.log('Using deprecated function getPendingNonExchangeTxs()')
  return getAssetPendingNonExchangeTxs('ethereum', activeWalletAccount, getTxLog, getIsExchangeTx)
}

export function getAssetPendingNonExchangeTxs(
  baseAssetName,
  activeWalletAccount,
  getTxLog,
  getIsExchangeTx
) {
  // check if the tx log even has pending transactions
  const result = isQueuedPendingTx(
    { data: { nonce: Number.MAX_SAFE_INTEGER } },
    baseAssetName,
    activeWalletAccount,
    getTxLog
  )
  if (!result) return []

  const txLog = getTxLog(baseAssetName, activeWalletAccount)
  const pendingNonExchangeTxs = []
  for (let index = isQueuedPendingTx.minIndex; index < txLog.size; index++) {
    const _tx = txLog.getAt(index)
    if (_tx.pending && _tx.sent && !getIsExchangeTx(_tx.txId)) {
      pendingNonExchangeTxs.push(_tx)
    }
  }

  return pendingNonExchangeTxs
}

function isQueuedPendingTx(tx, baseAssetName, activeWalletAccount, getTxLog) {
  const txLog = getTxLog(baseAssetName, activeWalletAccount)
  if (!txLog || txLog.size === 0) return false

  const txAtMinIndex = txLog.getAt(isQueuedPendingTx.minIndex)
  if (isQueuedPendingTx.minIndex >= 0 && txAtMinIndex && txAtMinIndex.pending) {
    return tx.data && tx.data.nonce > txAtMinIndex.data.nonce
  }

  let minPendingNonce = Number.MAX_SAFE_INTEGER
  for (let index = 0; index < txLog.size; index++) {
    const _tx = txLog.getAt(index)
    if (!_tx.pending) continue

    const nonce = _tx.data && _tx.data.nonce
    if (nonce === undefined) continue

    // ignore nonce of a TX that just replaced an another tx
    if (_tx.data.replacedTxId && Date.now() - _tx.date < MINUTE) continue
    if (_tx.sent && nonce < minPendingNonce) {
      minPendingNonce = nonce
      isQueuedPendingTx.minIndex = index
    }
  }

  return tx.data.nonce > minPendingNonce
}

// needs for tests
export const _refreshCache = () => {
  isQueuedPendingTx.minPendingNonce = null
  isQueuedPendingTx.minIndex = null
}

const wrapResponseToObject = ({ bumpType = BumpType.NONE, errorMessage = null } = {}) => ({
  bumpType,
  errorMessage,
})

const calculateTxGasPrice = (tx) => tx.feeAmount.div(tx.data.gasLimit)

export const calculateBumpedGasPrice = ({ baseAsset, tx, currentGasPrice, eip1559Enabled }) => {
  const usedGasPrice = calculateTxGasPrice(tx)
  const gasPrice = currentGasPrice.gt(usedGasPrice) ? currentGasPrice : usedGasPrice
  const bumpedGasPrice = gasPrice.mul(BUMP_RATE)

  return eip1559Enabled && tx.data?.tipGasPrice
    ? {
        bumpedTipGasPrice: baseAsset.currency
          .baseUnit(tx.data.tipGasPrice)
          .to('Gwei')
          .mul(BUMP_RATE),
        bumpedGasPrice,
      }
    : { bumpedGasPrice }
}

export const canAccelerateTx = ({
  tx,
  assets,
  getFeeData,
  activeWalletAccount,
  getPersonalNoteByTxId,
  getIsRbfEnabled,
  getIsEnoughBalanceToAccelerate,
  getTxLog,
  getIsExchangeTx,
}) => {
  const assetName = tx.coinName
  const baseAsset = assets[assetName].baseAsset
  const baseAssetName = baseAsset.name
  if (!getIsRbfEnabled(assetName)) return wrapResponseToObject({ errorMessage: 'rbf is disabled' })
  if (!isEthereumLike(assets[assetName]))
    return wrapResponseToObject({ errorMessage: `not an ETH/ERC20/BSC/BEP20 asset supplied` })
  if (!tx.pending || !tx.sent)
    return wrapResponseToObject({ errorMessage: 'can not bump a confirmed or received TX' })
  if (!tx.data || !(tx.data.gasLimit || tx.data.tipGasPrice))
    return wrapResponseToObject({ errorMessage: 'data object is missing or corrupted' })
  const isExchangeTx = getIsExchangeTx(tx.txId)
  if (isExchangeTx) return wrapResponseToObject({ errorMessage: 'can not bump an exchange TX' })
  const personalNote = getPersonalNoteByTxId(tx.txId)
  if (personalNote && personalNote.dapp)
    return wrapResponseToObject({ errorMessage: 'can not bump a dapp TX' })
  if (isQueuedPendingTx(tx, baseAssetName, activeWalletAccount, getTxLog))
    return wrapResponseToObject({ errorMessage: 'there is a stuck TX with lower nonce' })

  const { gasPrice: currentGasPrice, eip1559Enabled } = getFeeData(assetName)

  if (calculateTxGasPrice(tx).gte(currentGasPrice))
    return wrapResponseToObject({ errorMessage: 'the used gas price is still high enough' })

  const { bumpedGasPrice: gasPriceToUse } = calculateBumpedGasPrice({
    baseAsset,
    tx,
    currentGasPrice,
    eip1559Enabled,
  })
  const replacementFee = gasPriceToUse.mul(tx.data.gasLimit)
  const extraEthNeeded = replacementFee.sub(tx.feeAmount)
  if (!getIsEnoughBalanceToAccelerate(activeWalletAccount, baseAssetName, extraEthNeeded))
    return wrapResponseToObject({ errorMessage: 'insufficient funds' })

  return wrapResponseToObject({ bumpType: BumpType.RBF })
}

const getCanAccelerateTxFactory = (
  getFeeDataSelector,
  getFeeSelector,
  activeWalletAccountSelector,
  getPersonalNoteByTxIdSelector,
  getIsRbfEnabledSelector,
  getIsEnoughBalanceToAccelerateSelector,
  getTxLogSelector,
  getIsExchangeTxSelector,
  assetsSelector
) =>
  createSelector(
    getFeeDataSelector,
    getFeeSelector,
    activeWalletAccountSelector,
    getPersonalNoteByTxIdSelector,
    getIsRbfEnabledSelector,
    getIsEnoughBalanceToAccelerateSelector,
    getTxLogSelector,
    getIsExchangeTxSelector,
    assetsSelector,
    (
      getFeeData,
      getFee,
      activeWalletAccount,
      getPersonalNoteByTxId,
      getIsRbfEnabled,
      getIsEnoughBalanceToAccelerate,
      getTxLog,
      getIsExchangeTx,
      assets
    ) =>
      (tx) =>
        canAccelerateTx({
          tx,
          assets,
          getFeeData,
          getFee,
          activeWalletAccount,
          getPersonalNoteByTxId,
          getIsRbfEnabled,
          getIsEnoughBalanceToAccelerate,
          getTxLog,
          getIsExchangeTx,
        })
  )

export default getCanAccelerateTxFactory
